
import { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "@/components/ui/card";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  Users,
  Tag,
  Search,
  ArrowRight,
  Lightbulb
} from "lucide-react";
import { PriceElasticityCard } from "@/components/pricing/PriceElasticityCard";
import { TimedPricingRecommendation } from "@/components/pricing/TimedPricingRecommendation";
import { BundlingOpportunity } from "@/components/pricing/BundlingOpportunity";

// Sample data for menu items with elasticity
const menuItemsWithElasticity = [
  { 
    id: 1, 
    name: 'Papas Arrugadas con Mojo', 
    category: 'Mains', 
    currentPrice: 32.99, 
    cost: 12.50, 
    elasticity: -1.5, 
    optimalPrice: 35.99,
    projectedSales: 42,
    projectedRevenue: 1511.58,
    competitiveIndex: 0.95
  },
  { 
    id: 2, 
    name: 'Caldo de Pescado', 
    category: 'Starters', 
    currentPrice: 15.99, 
    cost: 8.75, 
    elasticity: -0.8, 
    optimalPrice: 17.99,
    projectedSales: 65,
    projectedRevenue: 1169.35,
    competitiveIndex: 1.05
  },
  { 
    id: 3, 
    name: 'Risotto de Gofio', 
    category: 'Mains', 
    currentPrice: 28.50, 
    cost: 14.25, 
    elasticity: -1.2, 
    optimalPrice: 26.99,
    projectedSales: 48,
    projectedRevenue: 1295.52,
    competitiveIndex: 0.98
  },
  { 
    id: 4, 
    name: 'Ensalada Canaria', 
    category: 'Starters', 
    currentPrice: 12.99, 
    cost: 3.50, 
    elasticity: -0.4, 
    optimalPrice: 14.99,
    projectedSales: 89,
    projectedRevenue: 1334.11,
    competitiveIndex: 1.12
  },
  { 
    id: 5, 
    name: 'Conejo al Salmorejo', 
    category: 'Mains', 
    currentPrice: 29.99, 
    cost: 11.25, 
    elasticity: -1.8, 
    optimalPrice: 27.99,
    projectedSales: 38,
    projectedRevenue: 1063.62,
    competitiveIndex: 0.92
  }
];

// Sample data for time-based pricing
const timePricingOpportunities = [
  {
    id: 1,
    timeSlot: 'Monday-Thursday (3-5pm)',
    strategy: 'Happy Hour',
    items: ['Vinos locales', 'Cervezas de barril', 'Tapas selectas'],
    discount: 25,
    projectedSales: 35,
    revenueImpact: 420.50,
    customerIncrease: 18
  },
  {
    id: 2,
    timeSlot: 'Tuesday All Day',
    strategy: 'Martes de Tapas',
    items: ['Tapas especiales', 'Sangría'],
    discount: 20,
    projectedSales: 48,
    revenueImpact: 576.00,
    customerIncrease: 22
  },
  {
    id: 3,
    timeSlot: 'Friday-Saturday (9-11pm)',
    strategy: 'Menú Nocturno',
    items: ['Pinchos', 'Cócteles'],
    discount: 15,
    projectedSales: 42,
    revenueImpact: 630.00,
    customerIncrease: 15
  },
  {
    id: 4,
    timeSlot: 'Sunday (10am-2pm)',
    strategy: 'Brunch Isleño',
    items: ['Platos de brunch', 'Barraquito', 'Zumos frescos'],
    discount: 0,
    projectedSales: 65,
    revenueImpact: 1625.00,
    customerIncrease: 28
  }
];

// Sample data for occupancy-based pricing
const occupancyPricingRules = [
  {
    id: 1,
    occupancyRange: 'Under 40%',
    strategy: 'Oferta Relámpago',
    items: 'Platos principales selectos',
    discount: 20,
    notification: 'Notificación push + tarjetas en mesa'
  },
  {
    id: 2,
    occupancyRange: '40-60%',
    strategy: 'Menú Combinado',
    items: 'Paquetes de entrante + plato principal',
    discount: 15,
    notification: 'Sugerencias del camarero'
  },
  {
    id: 3,
    occupancyRange: '60-80%',
    strategy: 'Precio Estándar',
    items: 'Todos los platos',
    discount: 0,
    notification: 'Ninguna'
  },
  {
    id: 4,
    occupancyRange: '80-100%',
    strategy: 'Experiencia Premium',
    items: 'Especialidades del chef y platos limitados',
    discount: -10,
    notification: 'Destacados por el camarero'
  }
];

// Sample data for competitor pricing
const competitorPricing = [
  {
    id: 1,
    competitor: 'Restaurante La Marea',
    distance: '0.3 mi',
    item: 'Cherne a la Plancha',
    theirPrice: 35.99,
    ourPrice: 32.99,
    difference: -8.3
  },
  {
    id: 2,
    competitor: 'Casa Vieja',
    distance: '0.5 mi',
    item: 'Caldo de Pescado',
    theirPrice: 14.99,
    ourPrice: 15.99,
    difference: 6.7
  },
  {
    id: 3,
    competitor: 'El Tenedor de Oro',
    distance: '0.8 mi',
    item: 'Fideos con Mojo',
    theirPrice: 22.99,
    ourPrice: 19.99,
    difference: -13.0
  },
  {
    id: 4,
    competitor: 'Terraza del Puerto',
    distance: '1.2 mi',
    item: 'Vieja a la Espalda',
    theirPrice: 27.99,
    ourPrice: 26.99,
    difference: -3.6
  }
];

// Sample data for bundling opportunities
const bundlingOpportunities = [
  {
    id: 1,
    name: 'Noche Romántica Canaria',
    description: 'Entrante, dos platos principales, postre para compartir, y una botella de vino',
    includedItems: ['Entrante a elegir', 'Dos platos principales a elegir', 'Un postre', 'Botella de vino local'],
    regularPrice: 120.96,
    bundlePrice: 99.99,
    discount: 17.3,
    ingredients: ['Pan', 'Queso', 'Cherne', 'Conejo', 'Gofio', 'Chocolate'],
    overstock: ['Vino', 'Chocolate']
  },
  {
    id: 2,
    name: 'Festín Familiar',
    description: 'Ensalada grande, cuatro platos principales, dos guarniciones, y cuatro postres',
    includedItems: ['Ensalada grande de la casa', 'Cuatro platos principales a elegir', 'Dos guarniciones', 'Cuatro postres'],
    regularPrice: 175.96,
    bundlePrice: 149.99,
    discount: 14.8,
    ingredients: ['Lechuga', 'Tomates', 'Pollo', 'Carne', 'Papas', 'Verduras'],
    overstock: ['Lechuga', 'Tomates']
  },
  {
    id: 3,
    name: 'Experiencia Marítima',
    description: 'Dos entrantes de marisco, dos platos principales de pescado, y una botella de vino blanco',
    includedItems: ['Dos entrantes de marisco', 'Dos platos principales de pescado', 'Botella de vino blanco'],
    regularPrice: 135.96,
    bundlePrice: 115.99,
    discount: 14.7,
    ingredients: ['Gambas', 'Lapas', 'Vieja', 'Cherne', 'Vino blanco'],
    overstock: ['Lapas', 'Vino blanco']
  }
];


const DynamicPricingEngine = () => {
  const [activeTab, setActiveTab] = useState("elasticity");
  const [selectedDish, setSelectedDish] = useState<number | null>(null);
  const [newPrice, setNewPrice] = useState("");
  const [revenuePrediction, setRevenuePrediction] = useState<number | null>(null);

  const handlePriceSim = () => {
    if (selectedDish && newPrice) {
      // Simple simulation for demo purposes
      const dish = menuItemsWithElasticity.find(item => item.id === selectedDish);
      if (dish) {
        const priceChange = (parseFloat(newPrice) - dish.currentPrice) / dish.currentPrice;
        const volumeChange = priceChange * dish.elasticity;
        const newVolume = Math.round(dish.projectedSales * (1 + volumeChange));
        const predictedRevenue = newVolume * parseFloat(newPrice);
        setRevenuePrediction(predictedRevenue);
      }
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-semibold tracking-tight">Dynamic Pricing Engine</h1>
      </div>

      <Tabs defaultValue="elasticity" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="elasticity" onClick={() => setActiveTab("elasticity")}>
            Price Elasticity
          </TabsTrigger>
          <TabsTrigger value="time-based" onClick={() => setActiveTab("time-based")}>
            Time-Based Pricing
          </TabsTrigger>
          <TabsTrigger value="revenue-impact" onClick={() => setActiveTab("revenue-impact")}>
            Revenue Modeling
          </TabsTrigger>
          <TabsTrigger value="competitive" onClick={() => setActiveTab("competitive")}>
            Competitive Analysis
          </TabsTrigger>
          <TabsTrigger value="bundling" onClick={() => setActiveTab("bundling")}>
            Bundling Opportunities
          </TabsTrigger>
        </TabsList>

        {/* Price Elasticity Tab */}
        <TabsContent value="elasticity" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Price Elasticity Analysis</CardTitle>
              <CardDescription>
                Analyze how demand for menu items changes with price adjustments
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {menuItemsWithElasticity.slice(0, 3).map(item => (
                  <PriceElasticityCard key={item.id} item={item} />
                ))}
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Current Price</TableHead>
                    <TableHead>Optimal Price</TableHead>
                    <TableHead>Elasticity</TableHead>
                    <TableHead>Recommendation</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {menuItemsWithElasticity.map(item => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell>${item.currentPrice.toFixed(2)}</TableCell>
                      <TableCell>${item.optimalPrice.toFixed(2)}</TableCell>
                      <TableCell>{item.elasticity.toFixed(1)}</TableCell>
                      <TableCell>
                        {item.optimalPrice > item.currentPrice ? (
                          <Badge className="bg-green-500">
                            <TrendingUp className="mr-1 h-3 w-3" />
                            Increase Price
                          </Badge>
                        ) : (
                          <Badge className="bg-blue-500">
                            <TrendingDown className="mr-1 h-3 w-3" />
                            Decrease Price
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Time-Based Pricing Tab */}
        <TabsContent value="time-based" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Time & Occupancy Based Pricing</CardTitle>
                <CardDescription>
                  Optimize pricing based on day, time, and restaurant occupancy
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center">
                      <Clock className="mr-2 h-5 w-5 text-muted-foreground" /> 
                      Time-Based Pricing Opportunities
                    </h3>
                    
                    <div className="space-y-4">
                      {timePricingOpportunities.map(opportunity => (
                        <TimedPricingRecommendation key={opportunity.id} opportunity={opportunity} />
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center">
                      <Users className="mr-2 h-5 w-5 text-muted-foreground" /> 
                      Occupancy-Based Pricing Rules
                    </h3>
                    
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Occupancy</TableHead>
                          <TableHead>Strategy</TableHead>
                          <TableHead>Discount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {occupancyPricingRules.map(rule => (
                          <TableRow key={rule.id}>
                            <TableCell>{rule.occupancyRange}</TableCell>
                            <TableCell>{rule.strategy}</TableCell>
                            <TableCell>
                              {rule.discount > 0 ? (
                                <Badge className="bg-green-500">-{rule.discount}%</Badge>
                              ) : rule.discount < 0 ? (
                                <Badge className="bg-amber-500">+{Math.abs(rule.discount)}%</Badge>
                              ) : (
                                <Badge variant="outline">No change</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Revenue Modeling Tab */}
        <TabsContent value="revenue-impact" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Revenue Impact Modeling</CardTitle>
              <CardDescription>
                Simulate the impact of price changes on revenue before implementation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2">
                  <h3 className="text-lg font-semibold mb-4">Simulation Tool</h3>
                  
                  <div className="space-y-4 p-4 border rounded-md">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="dish">Select Dish</Label>
                        <select 
                          id="dish"
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          onChange={(e) => setSelectedDish(Number(e.target.value))}
                        >
                          <option value="">Select a dish</option>
                          {menuItemsWithElasticity.map(item => (
                            <option key={item.id} value={item.id}>{item.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="price">New Price ($)</Label>
                        <Input 
                          id="price" 
                          type="number" 
                          min="0" 
                          step="0.01"
                          value={newPrice}
                          onChange={(e) => setNewPrice(e.target.value)}
                          placeholder="0.00" 
                        />
                      </div>
                      <div className="flex items-end">
                        <Button onClick={handlePriceSim} className="w-full">
                          Simulate Impact
                          <ArrowRight className="ml-1 h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {revenuePrediction && (
                      <div className="mt-4 p-4 bg-muted rounded-md">
                        <h4 className="font-semibold mb-2">Simulation Results</h4>
                        <p className="text-lg">
                          Predicted Revenue: <span className="font-bold text-green-600">${revenuePrediction.toFixed(2)}</span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <Lightbulb className="mr-2 h-5 w-5 text-muted-foreground" />
                    Quick Tips
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                      <p><strong>Elastic items</strong> (elasticity &lt; -1) are more sensitive to price changes.</p>
                    </div>
                    <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-md">
                      <p><strong>Inelastic items</strong> (elasticity &gt; -1) can be priced higher with minimal impact on demand.</p>
                    </div>
                    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-md">
                      <p>Consider <strong>psychological pricing</strong> points (e.g., $9.99 instead of $10.00).</p>
                    </div>
                    <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-md">
                      <p>Test price changes on <strong>a subset of items</strong> before implementing widely.</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Competitive Analysis Tab */}
        <TabsContent value="competitive" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Competitive Price Analysis</CardTitle>
              <CardDescription>
                Compare your menu prices with nearby competitors
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center mb-6 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input className="pl-10" placeholder="Search for competitors or menu items..." />
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Competitor</TableHead>
                    <TableHead>Distance</TableHead>
                    <TableHead>Menu Item</TableHead>
                    <TableHead>Their Price</TableHead>
                    <TableHead>Our Price</TableHead>
                    <TableHead>Difference</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {competitorPricing.map(item => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.competitor}</TableCell>
                      <TableCell>{item.distance}</TableCell>
                      <TableCell>{item.item}</TableCell>
                      <TableCell>${item.theirPrice.toFixed(2)}</TableCell>
                      <TableCell>${item.ourPrice.toFixed(2)}</TableCell>
                      <TableCell>
                        {item.difference < 0 ? (
                          <Badge className="bg-green-500">{item.difference}%</Badge>
                        ) : (
                          <Badge className="bg-red-500">+{item.difference}%</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bundling Opportunities Tab */}
        <TabsContent value="bundling" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Bundling Opportunities</CardTitle>
              <CardDescription>
                Create strategic bundles to utilize current inventory and maximize revenue
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {bundlingOpportunities.map(bundle => (
                  <BundlingOpportunity key={bundle.id} bundle={bundle} />
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DynamicPricingEngine;
